import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { settingsRepository } from '@/data/settings/repositories/settings_repository_impl'
import { ChangeAdminPasswordUseCase } from '@/domain/settings/usecases/change_admin_password.usecase'
import { extractRtkErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { ChangePasswordFormErrors } from '../models/settings_ui_state'

const defaultChangePassword = new ChangeAdminPasswordUseCase(settingsRepository)

export interface UseChangePasswordViewModelDeps {
  changePassword?: ChangeAdminPasswordUseCase
}

export function useChangePasswordViewModel(deps: UseChangePasswordViewModelDeps = {}) {
  const navigate = useNavigate()
  const changePassword = deps.changePassword ?? defaultChangePassword

  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<ChangePasswordFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validate = useCallback((): ChangePasswordFormErrors => {
    const next: ChangePasswordFormErrors = {}
    if (!oldPassword.trim()) {
      next.oldPassword = 'Old password is required'
    }
    if (!newPassword.trim()) {
      next.newPassword = 'New password is required'
    } else if (newPassword.length < 6) {
      next.newPassword = 'New password must be at least 6 characters'
    }
    if (!confirmPassword.trim()) {
      next.confirmPassword = 'Confirm password is required'
    } else if (newPassword !== confirmPassword) {
      next.confirmPassword = 'Passwords do not match'
    }
    return next
  }, [confirmPassword, newPassword, oldPassword])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsSubmitting(true)

      const validationErrors = validate()
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
        setIsSubmitting(false)
        return
      }

      try {
        await changePassword.execute({
          currentPassword: oldPassword,
          newPassword,
        })
        alert('Password changed successfully!')
        setOldPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setErrors({})
      } catch (error: unknown) {
        logError('Failed to change password:', error)
        alert(extractRtkErrorMessage(error))
      } finally {
        setIsSubmitting(false)
      }
    },
    [changePassword, newPassword, oldPassword, validate]
  )

  const handleCancel = useCallback(() => {
    navigate('/admin')
  }, [navigate])

  return {
    oldPassword,
    newPassword,
    confirmPassword,
    errors,
    isSubmitting,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    setErrors,
    handleSubmit,
    handleCancel,
  }
}
